/** Formatting for the usage page: durations, how long ago, and a short device label from a user agent. */

type Translate = (key: string, options?: Record<string, unknown>) => string

/** "45 s", "12 min", "2 h", "2 h 14 min" (units from the UI language). */
export function formatDuration(seconds: number, t: Translate): string {
    const total = Math.max(0, Math.round(seconds))
    if (total < 60) return t('usage.dur.s', { s: total })
    const minutes = Math.round(total / 60)
    if (minutes < 60) return t('usage.dur.m', { m: minutes })
    const h = Math.floor(minutes / 60)
    return minutes % 60 === 0 ? t('usage.dur.h', { h }) : t('usage.dur.hm', { h, m: minutes % 60 })
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 3600],
    ['month', 30 * 24 * 3600],
    ['day', 24 * 3600],
    ['hour', 3600],
    ['minute', 60],
]

/** "3 days ago", "yesterday", "in a minute" style, in the UI language; '' for no date. */
export function relativeTime(iso: string | null, now: number, locale: string): string {
    if (!iso) return ''
    const then = Date.parse(iso)
    if (Number.isNaN(then)) return ''
    const seconds = (then - now) / 1000
    const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    for (const [unit, size] of UNITS) {
        if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit)
    }
    return format.format(0, 'minute')
}

/** Date and time in the reader's time zone ("6. okt 2026, 10:05"); '' for no date. */
export function absoluteTime(iso: string | null, locale: string): string {
    if (!iso) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

/** "Chrome · macOS", "Safari · iPhone"; a non-browser client (a script) as its own name. */
export function deviceLabel(userAgent: string | null): string {
    const ua = (userAgent ?? '').trim()
    if (!ua) return ''
    if (!ua.startsWith('Mozilla/')) return ua.slice(0, 24)
    const browser = /Edg\//.test(ua)
        ? 'Edge'
        : /OPR\//.test(ua)
          ? 'Opera'
          : /Firefox\//.test(ua)
            ? 'Firefox'
            : /HeadlessChrome\//.test(ua)
              ? 'Headless Chrome'
              : /Chrome\//.test(ua)
                ? 'Chrome'
                : /Version\/.*Safari\//.test(ua)
                  ? 'Safari'
                  : ''
    const os = /iPhone/.test(ua)
        ? 'iPhone'
        : /iPad/.test(ua)
          ? 'iPad'
          : /Android/.test(ua)
            ? 'Android'
            : /Windows/.test(ua)
              ? 'Windows'
              : /Mac OS X/.test(ua)
                ? 'macOS'
                : /Linux/.test(ua)
                  ? 'Linux'
                  : ''
    return [browser, os].filter(Boolean).join(' · ')
}
